import dayjs from 'dayjs';
import { isNil, omitBy } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { Link, useHistory, useLocation } from 'react-router-dom';

import ConfirmModal from '../../app/modals/ConfirmModal';
import NotificationErrorModal from '../../app/notifications/modals/NotificationErrorModal';
import NotificationCalendarPresenter from '../../app/notifications/presenters/NotificationCalendarPresenter';
import NotificationDetailPresenter from '../../app/notifications/presenters/NotificationDetailPresenter';
import NotificationFormObj, { INotificationFields, NOTIFICATION_FIELD_KEYS } from '../../app/notifications/validates/notificationSchema';
import Breadcrumbs from '../../components/Breadcrumbs';
import Card from '../../components/Card';
import ToastComponent, { IToastType } from '../../components/Toast/ToastComponent';
import {
  ClientPlatform,
  CreatePintoAnnouncementNotificationInput,
  PintoAnnouncementNotificationsSortBy,
  PintoAnnouncementNotificationType,
  SortType,
} from '../../core/graphql/types';
import useMutationPintoAnnouncementNotification from '../../hooks/useMutationPintoAnnouncementNotification';
import useQueryPintoAnnouncementNotifications from '../../hooks/useQueryPintoAnnouncementNotifications';
import { convertFileToBase64 } from '../../utils/blob';
import useHookForm from '../../utils/form/useHookForm';

const CreateNotificationPage = () => {
  const history = useHistory();
  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const discountId = searchParams.get('id');
  const calendar = searchParams.get('calendar');

  const { pintoAnnouncementNotifications, fetchPintoAnnouncementNotifications, isLoadingPintoAnnouncementNotifications } = useQueryPintoAnnouncementNotifications();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);

  const { onMutatePintoCreateAnnouncementNotification } = useMutationPintoAnnouncementNotification();

  const { methods, onValidate, watch, setValue } = useHookForm<INotificationFields>({
    objectShapeForm: NotificationFormObj,
    mode: 'onChange',
    defaultValues: {
      [NOTIFICATION_FIELD_KEYS.PLATFORMS]: ['IOS', 'ANDROID', 'WEB'],
      [NOTIFICATION_FIELD_KEYS.TARGET_TYPE]: 'all-users',
      [NOTIFICATION_FIELD_KEYS.TARGET_SEGMENT_TYPE]: 'upload',
    },
  });

  const [title, description, targetSegmentType, targetType, url, platforms, sendAt, targets] = watch([
    NOTIFICATION_FIELD_KEYS.TITLE,
    NOTIFICATION_FIELD_KEYS.DESCRIPTION,
    NOTIFICATION_FIELD_KEYS.TARGET_SEGMENT_TYPE,
    NOTIFICATION_FIELD_KEYS.TARGET_TYPE,
    NOTIFICATION_FIELD_KEYS.URL,
    NOTIFICATION_FIELD_KEYS.PLATFORMS,
    NOTIFICATION_FIELD_KEYS.SEND_AT,
    NOTIFICATION_FIELD_KEYS.TARGETS,
  ]);

  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showConfirmTerminateDiscount, setShowConfirmTerminateDiscount] = useState<boolean>(false);
  const [selectedCSVFile, setSelectedCSVFile] = useState<File | undefined>(undefined);

  // const [errorItems, setErrorsItems] = useState<IErrorItem[] | undefined>(undefined);

  useEffect(() => {
    fetchPintoAnnouncementNotifications({
      limit: 100,
      sortBy: PintoAnnouncementNotificationsSortBy.Notified,
      sortType: SortType.Desc,
    });
  }, [fetchPintoAnnouncementNotifications]);

  const onSelectCSVFile = useCallback(
    (file: File | undefined) => {
      setSelectedCSVFile(file);
      setValue(NOTIFICATION_FIELD_KEYS.FILE_NAME, file?.name as never, {
        shouldValidate: true,
      });
    },
    [setValue],
  );

  const mapPlatformLabelsToEnums = (values: string[]): ClientPlatform[] => {
    return values
      .map((val) => {
        switch (val.toUpperCase()) {
          case 'IOS':
            return ClientPlatform.Apple;
          case 'ANDROID':
            return ClientPlatform.Google;
          case 'WEB':
            return ClientPlatform.Web;
          default:
            return undefined;
        }
      })
      .filter(Boolean) as ClientPlatform[];
  };

  const mapTargetTypeToEnum = (value: string): PintoAnnouncementNotificationType => {
    return value === 'specific-user' ? PintoAnnouncementNotificationType.SpecificUser : PintoAnnouncementNotificationType.All;
  };

  const onSubmitPublishNotification = useCallback(async () => {
    setShowConfirmModal(false);

    if ((targetSegmentType === 'upload' && targetType === 'specific-user' && !selectedCSVFile) || !title) return;

    const fileBase64 = selectedCSVFile ? ((await convertFileToBase64(selectedCSVFile)) as string) : undefined;

    const input: CreatePintoAnnouncementNotificationInput = {
      title,
      description,
      // clickActionType: PintoAnnouncementNotificationClickActionType.Link,
      clickActionData: url || undefined,
      platforms: mapPlatformLabelsToEnums(platforms),
      notifiedAt: dayjs(sendAt).set('millisecond', 0).toISOString(),
      type: mapTargetTypeToEnum(targetType),
      userIds: targetSegmentType === 'search' ? targets : undefined,
      userListFileData: fileBase64,
    };

    const cleanedInput = omitBy(input, isNil) as CreatePintoAnnouncementNotificationInput;
    const res = await onMutatePintoCreateAnnouncementNotification(cleanedInput);

    if (res.success) {
      ToastComponent({ label: 'Publish successfully ', type: IToastType.SUCCESS });
      history.push('/notifications');
    } else {
      if (res.errors) {
        const _errorMessage = res.errors[0].message || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง';
        setErrorMessage(_errorMessage);
        setShowErrorModal(true);
      }
    }
  }, [targetSegmentType, targetType, selectedCSVFile, title, description, url, platforms, sendAt, targets, onMutatePintoCreateAnnouncementNotification, history]);

  const handlePublishScheduledDiscount = useCallback(async () => {
    const isValid = await onValidate();
    // console.log('🚨 Validation Errors:', methods.formState.errors);
    if (isValid) {
      // console.log('✅ Form Values:', methods.getValues());
      setShowConfirmModal(true);
    }
  }, [onValidate]);

  return (
    <div>
      <>
        {showConfirmModal && (
          <ConfirmModal
            onClose={() => setShowConfirmModal(false)}
            onConFirm={onSubmitPublishNotification}
            title="Publish"
            subTitle="Schedule will automatically publish when in due course,
          Are you confirm to publish?"
          />
        )}

        {showConfirmTerminateDiscount && (
          <ConfirmModal
            onClose={() => setShowConfirmTerminateDiscount(false)}
            onConFirm={() => undefined}
            title="Terminate"
            subTitle="Discount will be cancel and price of e-book will change
          to original price. Are you confirm to terminate?"
          />
        )}

        {showErrorModal && (
          <NotificationErrorModal
            onConFirm={() => {
              setShowErrorModal(false);
            }}
            errorMessage={errorMessage}
          />
        )}

        {/* {targetSegmentType === 'search' ? (
          <SelectUserListModal
            setFormTargets={(userIds) => {
              methods.setValue('targets', userIds, { shouldValidate: true });
              console.log('✅ Set targets in form:', userIds);
            }}
          />
        ) : null} */}

        {isLoadingPintoAnnouncementNotifications && <div className="w-full btn btn-outline btn-disabled bg-systemGrays04LabelQuaternary border-none loading h-[100px]" />}
        {!isLoadingPintoAnnouncementNotifications && (
          <>
            {calendar ? (
              <>
                <div className="sticky top-0 z-50 px-5 bg-componentsBgGrouped02">
                  {/* section: breadcrumbs */}
                  <Breadcrumbs items={[{ name: 'Notifications', url: '/notifications' }, { name: `Notification Events` }]} />

                  {/* section: title */}
                  <div className="flex justify-between pt-5">
                    <h1 className="font-dbh text-[28px]">{`Notification Events`}</h1>

                    <div className="space-x-12">
                      <Link to="/notifications">
                        <button className="btn btn-outline">Back</button>
                      </Link>
                    </div>
                  </div>
                </div>

                <Card className="flex flex-row w-full">
                  <FormProvider {...methods}>
                    <NotificationCalendarPresenter
                      calenderWidth="100vw"
                      calenderHeight="80vh"
                      events={pintoAnnouncementNotifications?.edges.map(({ node }) => ({ title: node.title, start: node.notifiedAt })) ?? []}
                    />
                  </FormProvider>
                </Card>
              </>
            ) : (
              <>
                <div className="sticky top-0 z-50 px-5 bg-componentsBgGrouped02">
                  {/* section: breadcrumbs */}
                  <Breadcrumbs items={[{ name: 'Notifications', url: '/notifications' }, { name: `${discountId ? 'Edit' : 'Create New'} Notification` }]} />

                  {/* section: title */}
                  <div className="flex justify-between pt-5">
                    <h1 className="font-dbh text-[28px]">{`${discountId ? 'Edit' : 'Create New'} Notification`}</h1>

                    <div className="space-x-12">
                      <Link to="/notifications">
                        <button className="btn btn-outline">CANCEL</button>
                      </Link>
                      <button className="btn btn-error" onClick={handlePublishScheduledDiscount} disabled={false}>
                        Submit
                      </button>
                    </div>
                  </div>
                </div>

                <Card className="flex flex-row w-full space-x-24">
                  <div className="flex-1">
                    <FormProvider {...methods}>
                      <NotificationDetailPresenter selectedCSVFile={selectedCSVFile} onSelectCSVFile={onSelectCSVFile} />
                    </FormProvider>
                  </div>

                  {/* section: calendar */}
                  <div className="flex-[0.8] p-12">
                    <FormProvider {...methods}>
                      <NotificationCalendarPresenter events={pintoAnnouncementNotifications?.edges.map(({ node }) => ({ title: node.title, start: node.notifiedAt })) ?? []} />
                    </FormProvider>
                  </div>
                </Card>
              </>
            )}
          </>
        )}
      </>
    </div>
  );
};

export default CreateNotificationPage;
